import abTestsConfig from '@config/ab_tests.json'
import { inLocalEnvironment } from 'shared/javascripts/util/environment'
import { emit, on } from 'shared/javascripts/util/events'

// Registry of our A/B tests, backed by config/ab_tests.json (the single source of truth,
// shared with the SCSS `if-ab-test` mixin and the dev widget).
//
// The runtime happens to be Convert.com — addressed via the `window._conv_q` queue and the
// decided experiences in `window.convert` — but that's an implementation detail. Everything
// here is by friendly name (experiments, variants, goals, locations), so the magic Convert
// IDs live only in the JSON.

export interface AbTestExperiment {
  id: string
  ticket?: string
  variants: Record<string, string>
  original: string
}

interface AbTestsConfig {
  experiments: Record<string, AbTestExperiment>
  goals: Record<string, string>
  locations: Record<string, string>
}

const config: AbTestsConfig = abTestsConfig

export const experiments = config.experiments
export const goals = config.goals
export const locations = config.locations

type Config = typeof abTestsConfig
export type ExperimentName = keyof Config['experiments']
export type GoalName = keyof Config['goals']
export type LocationName = keyof Config['locations']
type VariantName<E extends ExperimentName> = keyof Config['experiments'][E]['variants']

class ABTests {
  boot() {
    window._conv_q = window._conv_q || []
    window._conv_q.push({
      what: 'addListener',
      params: {
        // https://support.convert.com/hc/en-us/articles/360056121112-experiment-life-cycle-events-executing-code-after-an-variation-has-been-decided-experiment-executed-or-goal-triggered#s4
        // Fires after every evaluation (page load, SPA navigation, triggerLocation), so it also
        // catches experiences dropped by a location mismatch, which `experience.activated` misses.
        event: 'snippet.experiences_evaluated',
        handler: function () {
          ABTests.debug('snippet.experiences_evaluated')
          emit('convert:experiences:updated')
        },
      },
    })
  }

  private static debug(...args: unknown[]) {
    if (inLocalEnvironment()) {
      console.log('[ABTests]', ...args)
    }
  }

  /**
   * Is `variantName` the current variant of the experiment `experimentName`?
   * Returns false while the experiment is still undecided, even if `variantName` is the
   * experiment's original.
   * **Note that the variant can change after the page has loaded.
   * Use onVariant to be notified of variant changes.**
   * @return whether the experiment (given by its friendly name) runs in the variant (given by its friendly name)
   */
  variantCurrentlyActive<E extends ExperimentName>(experimentName: E, variantName: VariantName<E>) {
    return this.getCurrentVariant(experimentName) === variantName
  }

  /**
   * Subscribe to changes of `experimentName`'s variant.
   * Called immediately if a variant has already been decided, and again on each change.
   * Not called at all while the experiment is undecided (no fallback to the original variant),
   * so callers can tell "still deciding" apart from "decided as original".
   * @return A destructor function you need to call to stop listening.
   */
  onVariant<E extends ExperimentName>(
    experimentName: E,
    callback: (variantName: VariantName<E>) => void,
  ) {
    let currentVariant: VariantName<E> | undefined
    const onExperienceUpdated = () => {
      const newVariant = this.getCurrentVariant(experimentName)
      if (newVariant !== undefined && newVariant !== currentVariant) {
        currentVariant = newVariant
        callback(newVariant)
      }
    }
    onExperienceUpdated()
    return on('convert:experiences:updated', onExperienceUpdated)
  }

  /**
   * Resolves with `experimentName`'s variant once Convert has decided it, or with undefined
   * if Convert doesn't decide within `timeoutMs` (e.g. blocked by an ad blocker).
   */
  decidedVariant<E extends ExperimentName>(
    experimentName: E,
    { timeoutMs }: { timeoutMs: number },
  ): Promise<VariantName<E> | undefined> {
    const currentVariant = this.getCurrentVariant(experimentName)
    if (currentVariant !== undefined) return Promise.resolve(currentVariant)

    return new Promise((resolve) => {
      const stopListening = this.onVariant(experimentName, (variant) => {
        clearTimeout(timeoutId)
        stopListening()
        resolve(variant)
      })
      const timeoutId = setTimeout(() => {
        stopListening()
        resolve(undefined)
      }, timeoutMs)
    })
  }

  conversion(goalName: GoalName) {
    ABTests.debug('triggerConversion', { goalName })
    window._conv_q = window._conv_q || []
    window._conv_q.push(['triggerConversion', this.goalIdFor(goalName)])
  }

  revenueConversion(goalName: GoalName, revenue: number, productsCount: number) {
    ABTests.debug('pushRevenue', { goalName, revenue, productsCount })
    window._conv_q = window._conv_q || []
    window._conv_q.push([
      'pushRevenue',
      revenue,
      productsCount,
      this.goalIdFor(goalName),
      'force_multiple',
    ])
  }

  triggerLocation(locationName: LocationName) {
    ABTests.debug('triggerLocation', { locationName })
    window._conv_q = window._conv_q || []
    window._conv_q.push({
      what: 'triggerLocation',
      params: { locationId: this.locationIdFor(locationName) },
    })
  }

  private experimentFor(experimentName: string): AbTestExperiment {
    const experiment = experiments[experimentName]
    if (!experiment) throw new Error(`Unknown A/B test "${experimentName}"`)
    return experiment
  }

  private goalIdFor(goalName: string): string {
    const id = goals[goalName]
    if (id == null) throw new Error(`Unknown A/B test goal "${goalName}"`)
    return id
  }

  private locationIdFor(locationName: string): string {
    const id = locations[locationName]
    if (id == null) throw new Error(`Unknown A/B test location "${locationName}"`)
    return id
  }

  // Returns the active variant name, or undefined when Convert hasn't decided the experiment
  // yet. Deliberately does NOT fall back to `experiment.original`: callers need to be able to
  // tell "still deciding" apart from "decided as original" (see onVariant). The `as
  // VariantName<E>` cast bridges the widened-to-`string` config values (variant keys) back to
  // the exact variant union.
  private getCurrentVariant<E extends ExperimentName>(
    experimentName: E,
  ): VariantName<E> | undefined {
    const experiment = this.experimentFor(experimentName)
    // Convert.js can hand back a numeric variation id, so coerce before comparing.
    const currentId = String(window.convert?.currentData?.experiences[experiment.id]?.variation?.id)

    for (const [variantName, variantId] of Object.entries(experiment.variants)) {
      if (variantId === currentId) {
        return variantName as VariantName<E>
      }
    }
    return undefined
  }
}

export default new ABTests()
